import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { GridColDef, GridColumnVisibilityModel, GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid';
import { useIntl } from 'react-intl';
import LanguageUtils from '../../../utils/LanguageUtils';
import TableUtils from '../../../utils/tableUtils';
import HelptextConstantsEnum from '../../../constants/helptextConstantsEnum';
import DataGridComponent from '../../common/dataGrid';
import TruncatedStringTooltip from '../../common/truncatedStringTooltip';

interface KickBackAgreementListProps {
    classes?: any;
    checkSelection?: any;
    isLoading?: boolean;
    multipleSelection?: boolean;
    componentsProps?: any;
    onPageChange?: any;
    rows?: any;
    applyRowFn?: any;
    hiddenColumns?: Array<string>;
    onSortModelChange?: any;
    columnVisibilityModel?: GridColumnVisibilityModel;
    onColumnVisibilityModelChange?: any;
    setCurrentComponentId?: any;
    disableLinks?: boolean;
}

const messages = {
    month: LanguageUtils.createMessage('Month'),
    year: LanguageUtils.createMessage('Year'),
    mainCustomer: LanguageUtils.createMessage('Main Customer'),
    customerName: LanguageUtils.createMessage('Customer Name'),
    fraktARE: LanguageUtils.createMessage('FraktARE'),
    kickbackARE: LanguageUtils.createMessage('KickbackARE'),
    fraktNordland: LanguageUtils.createMessage('FraktNordland'),
    kickBackNordland: LanguageUtils.createMessage('KickBack Nordland'),
    kickbackTOTAL: LanguageUtils.createMessage('Kickback TOTAL')
};

const KickBackAgreementList = forwardRef((props: KickBackAgreementListProps, ref: any): JSX.Element => {
    const { componentsProps, isLoading, checkSelection, onPageChange, rows, hiddenColumns, multipleSelection, onSortModelChange,
        columnVisibilityModel, onColumnVisibilityModelChange, setCurrentComponentId } = props;

    const defaultFn = (cellValues: GridRenderCellParams | GridValueGetterParams) => cellValues.row;

    const applyRowFn = props.applyRowFn || defaultFn;

    const dataGridRef = useRef();

    useImperativeHandle(ref, () => ({
        resetDataGridPage() {
            if(dataGridRef !== null && dataGridRef !== undefined && dataGridRef.current !== null && dataGridRef.current !== undefined) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                dataGridRef.current.resetPage();
            }
        }
    }));

    useEffect(() => {
        if(setCurrentComponentId !== undefined) {
            setCurrentComponentId(HelptextConstantsEnum.REPORT_KICKBACK_POSTEN);
        }
    }, []);

    function generateColumns(): GridColDef[] {
        const intl = useIntl();

        return [
            {
                field: 'month',
                headerName: intl.formatMessage(messages.month),
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <TruncatedStringTooltip value={applyRowFn(param)?.month} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('month', hiddenColumns)
            },
            {
                field: 'year',
                headerName: `${intl.formatMessage(messages.year)}`,
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <TruncatedStringTooltip value={applyRowFn(param)?.year} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('year', hiddenColumns)
            },
            {
                field: 'mainCustomer',
                headerName: intl.formatMessage(messages.mainCustomer),
                flex: 0.5,
                align: 'left',
                headerAlign: 'right',
                sortable: true,
                renderCell: (params: any) => {
                    return <TruncatedStringTooltip value={applyRowFn(params)?.mainCustomer} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('mainCustomer', hiddenColumns)
            },
            {
                field: 'customerNoName',
                headerName: intl.formatMessage(messages.customerName),
                flex: 1,
                align: 'left',
                headerAlign: 'right',
                sortable: true,
                renderCell: (params: any) => {
                    return <TruncatedStringTooltip value={applyRowFn(params)?.customerNoName} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('customerNoName', hiddenColumns)
            },
            {
                field: 'fraktARE',
                headerName: intl.formatMessage(messages.fraktARE),
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (params: any) => {
                    return <TruncatedStringTooltip value={applyRowFn(params)?.fraktARE} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('fraktARE', hiddenColumns)
            },
            {
                field: 'kickbackARE',
                headerName: intl.formatMessage(messages.kickbackARE),
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (params: any) => {
                    return <TruncatedStringTooltip value={applyRowFn(params)?.kickbackARE} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('kickbackARE', hiddenColumns)
            },
            {
                field: 'fraktNORDLAND',
                headerName: intl.formatMessage(messages.kickBackNordland),
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (params: any) => {
                    return <TruncatedStringTooltip value={applyRowFn(params)?.fraktNORDLAND} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('fraktNORDLAND', hiddenColumns)
            },
            {
                field: 'kickbackTOTAL',
                headerName: intl.formatMessage(messages.kickbackTOTAL),
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (params: any) => {
                    return <TruncatedStringTooltip value={applyRowFn(params)?.kickbackTOTAL} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('kickbackTOTAL', hiddenColumns)
            }
        ];
    }

    return (
        <DataGridComponent getRowId={(row) => row?.customerNoName + row?.amount + Math.random()} rows={rows} multipleSelection={multipleSelection} columns={generateColumns()}
            ref={dataGridRef} componentsProps={componentsProps} isLoading={isLoading}
            checkSelection={checkSelection} onPageChange={onPageChange} onSortModelChange={onSortModelChange}
            columnVisibilityModel={columnVisibilityModel} onColumnVisibilityModelChange={onColumnVisibilityModelChange} />
    );
});

export default KickBackAgreementList;
